import type { RichTextDocument } from "./rich-text-document.js";

export const ALWAYS_AI_ROLES = [
  "admin",
  "clinical_admin",
  "supervisor",
  "va",
  "rd",
] as const;
export type AlwaysAiRole = (typeof ALWAYS_AI_ROLES)[number];

export interface AlwaysAiUser {
  id: string;
  clerkId: string;
  name: string;
  email: string;
  /** Full app-scoped role set used for authorization. */
  roles: readonly AlwaysAiRole[];
  /** Highest-precedence role, retained for role-specific presentation. */
  role: AlwaysAiRole;
}

export interface HealthResponse {
  name: "always-ai";
  status: "ok" | "unavailable";
  environment: string;
  release: string;
}

export interface ApiSuccess<T> {
  data: T;
}
export interface ApiError {
  error: string;
  code?: string;
  existingRd?: {
    id?: string;
    slug?: string;
    name: string;
    supervisorName: string | null;
  };
}

export interface RdResponse {
  id: string;
  slug: string;
  name: string;
  supervisedByUserId: string | null;
  supervisorName: string | null;
  sessionCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface SessionResponse {
  id: string;
  shortId: string;
  rdId: string;
  clientName: string | null;
  sessionDate: string | null;
  sessionType: "initial" | "follow_up" | null;
  summary: string | null;
  status: "pending" | "analyzing" | "complete" | "error";
  errorMessage: string | null;
}

export type AppointmentStatus =
  | "scheduled"
  | "checked_in"
  | "occurred"
  | "no_show"
  | "cancelled"
  | "late_cancellation"
  | "rescheduled"
  | "unknown";

export interface AppointmentListItem {
  id: string;
  patientName: string | null;
  appointmentType: string;
  startsAt: string;
  endsAt: string | null;
  lengthMinutes: number | null;
  contactType: string | null;
  status: AppointmentStatus;
  statusLabel: string;
  /**
   * NO-428: present (and only ever true) when an automatic Enhancement's Draft
   * finished after the owning RD last opened the Appointment. List and Past
   * week rows only, on the RD's own lane, with soap-enhancement and
   * soap-auto-enhance on; the detail response never carries it.
   */
  draftReady?: true;
  /** NO-917: the visit's copy in Healthie; null on a visit NDD does not own. */
  mirrorState?: "pending" | "mirrored" | "failed" | null;
  /** NO-917: the latest action that wrote this visit, for the failed-copy Retry. */
  mirrorActionId?: string | null;
}

export interface AppointmentListRequest {
  timezone: string;
  showNonDefaultStatuses?: boolean;
  cursor?: string;
  /** The RD an admin selected as this read's target scope (NO-467). */
  rdId?: string;
  /** NO-767: read one Past week instead of the Upcoming list. */
  view?: "past";
  /** NO-767: the Past week's ISO Monday (YYYY-MM-DD); absent opens the newest week. */
  week?: string;
}

/**
 * One device-local Monday-to-Sunday week of ended owned Appointments (NO-767).
 * `weekStart` is null when there is no history at all; then `appointments` is
 * empty and both flags are false.
 */
export type AppointmentPastWeekResponse =
  | Extract<AppointmentListResponse, { status: "unavailable" | "no_selection" }>
  | {
      status: "available";
      timezone: string;
      now: string;
      healthieCalendarUrl: string;
      weekStart: string | null;
      /** Newest first; the whole week, with no page limit. */
      appointments: AppointmentListItem[];
      hasEarlier: boolean;
      hasLater: boolean;
    };

export type AppointmentListResponse =
  | {
      status: "unavailable";
      reason: "provider_unavailable" | "configuration_unavailable";
    }
  /**
   * An admin who has named no RD (NO-467). Distinct from `unavailable`, and
   * carries no schedule facts — not even a count, which was never fetched.
   */
  | { status: "no_selection" }
  | {
      status: "available";
      timezone: string;
      now: string;
      healthieCalendarUrl: string;
      appointments: AppointmentListItem[];
      /** Ended Appointments from the device-local current week, newest first; populated only on the first batch. */
      earlierAppointments: AppointmentListItem[];
      pageInfo: {
        hasNextPage: boolean;
        nextCursor: string | null;
      };
    };

/** One Client on an RD's own panel (NO-844). Visit times are ISO-8601 UTC. */
export interface PatientListItem {
  id: string;
  displayName: string | null;
  /** Null when the Client has no display name. */
  initials: string | null;
  lastVisitAt: string | null;
  /** Null when no visit is scheduled after now. */
  nextVisitAt: string | null;
}

/** `GET /api/patients`: the same scope answers as the Appointment list. */
export type PatientListResponse =
  | { status: "ok"; patients: PatientListItem[] }
  | { status: "no_selection" }
  | { status: "unavailable"; reason: "provider_unavailable" };

/** One row of the Messages list (NO-925): a Client and the latest message. */
export interface MessageThreadListItem {
  patientId: string;
  displayName: string | null;
  initials: string | null;
  /** Plain-text preview, at most 140 characters; "Message deleted" for a deleted message. */
  preview: string;
  /** ISO-8601 UTC send time of the latest message. */
  lastMessageAt: string;
}

/** `GET /api/messages/threads`: the same scope answers as the Patients list. */
export type MessageThreadListResponse =
  | { status: "ok"; threads: MessageThreadListItem[] }
  | { status: "no_selection" }
  | { status: "unavailable"; reason: "provider_unavailable" };

export interface MessageAttachmentView {
  fileName: string;
  kind: "document" | "image" | "audio";
}

export interface MessageView {
  id: string;
  authorKind: "staff" | "client";
  /** The sender's name; null when none is on file. */
  senderName: string | null;
  sentAt: string;
  editedAt: string | null;
  deleted: boolean;
  /** Null for a deleted message: its text never leaves the server. */
  body: string | null;
  /** Empty for a deleted message. */
  attachments: MessageAttachmentView[];
}

/** One RD's thread with the Client. `own` threads are the reader's lane's RD. */
export interface MessageThreadView {
  threadId: string;
  rdName: string | null;
  /** True for the thread of the RD whose Messages these are; false for an earlier RD's, read-only. */
  own: boolean;
  /** Oldest to newest. */
  messages: MessageView[];
  /**
   * Pass as `before` with this thread's id to read the page of messages older
   * than the first one here; null when this is the thread's start.
   */
  olderCursor: string | null;
}

export const MESSAGE_THREAD_PAGE_SIZE = 200;

/** `GET /api/messages/threads/:patientId`: earlier RDs' threads first, the RD's own last. */
export type MessageThreadResponse =
  | {
      status: "ok";
      client: { id: string; displayName: string | null };
      threads: MessageThreadView[];
    }
  | { status: "no_selection" }
  | { status: "unavailable"; reason: "provider_unavailable" };

export interface AppointmentNextOwnedVisit {
  startsAt: string;
}

export interface AppointmentDetailResponse extends AppointmentListItem {
  /** Opaque server-derived Appointment/Patient scope for memory cache isolation. */
  previousSoapNotesScope: string;
  nextOwnedVisit: AppointmentNextOwnedVisit | null;
}

export interface AppointmentNoteRevisionResponse {
  id: string;
  sequence: number;
  acceptedAt: string;
  digest: string;
}

export interface AppointmentNoteResponse {
  document: RichTextDocument;
  revision: AppointmentNoteRevisionResponse | null;
}

export interface AppointmentNoteSaveRequest {
  document: RichTextDocument;
  baseRevisionId: string | null;
  saveId: string;
}

export type AppointmentNoteSaveResponse = AppointmentNoteResponse;

export interface AppointmentNoteConflictResponse {
  error: "Appointment Note conflict";
  code: "appointment_note_conflict";
  current: AppointmentNoteResponse;
}

// --- Appointment Actual Duration (NO-861) ---
// Healthie's minutes for an Appointment, read with the service key and written
// by the worker as the requesting RD. Every route answers 404 while
// session-time-calculator is off for the signed-in user.

export interface AppointmentActualDurationResponse {
  data: { actualDurationMinutes: number | null };
}

/** POST body: the minutes to write and the value the RD saw (null = blank). */
export interface AppointmentActualDurationCreateRequest {
  minutes: number;
  expectedCurrentMinutes: number | null;
}

export type AppointmentActualDurationRequestStatus =
  | "running"
  | "succeeded"
  | "conflict"
  | "failed"
  | "cancelled";

/** The latest request's delivery state: never the minutes. */
export interface AppointmentActualDurationRequestState {
  requestId: string;
  attemptId: string;
  attemptNumber: number;
  status: AppointmentActualDurationRequestStatus;
  /** healthie_value_changed on conflict; healthie_rejected, healthie_unavailable or healthie_state_mismatch on failed. */
  safeErrorCode: string | null;
  requestedAt: string;
  completedAt: string | null;
}

export interface AppointmentActualDurationCreateResponse {
  data: { requestId: string; attemptId: string; status: "running" };
}

export interface AppointmentActualDurationLatestResponse {
  data: AppointmentActualDurationRequestState | null;
}

export interface AppointmentActualDurationRetryResponse {
  data: AppointmentActualDurationRequestState;
}

/** 409: a request is already running, or the latest one cannot be retried. */
export interface AppointmentActualDurationRefusalResponse {
  error: string;
  data: AppointmentActualDurationRequestState;
}

// --- Patient Quick Note (NO-445) ---
// One Patient-scoped note read and saved through an owned Appointment. Same
// document and Revision shape as the Appointment Note, so the autosave
// controller drives both.

/**
 * NO-784: where the current Revision came from. `healthie` is an edit made in
 * Healthie inside AlwaysAI's block; `healthie_merge` is the merged Revision
 * built from it.
 */
export type PatientQuickNoteRevisionSource = "alwaysai" | "healthie" | "healthie_merge";

export interface PatientQuickNoteRevisionResponse {
  id: string;
  sequence: number;
  acceptedAt: string;
  digest: string;
  /** NO-784: present only while patient-quick-note-healthie-import is on for the RD. */
  source?: PatientQuickNoteRevisionSource;
}

/**
 * NO-784: Healthie's quick notes text outside AlwaysAI's block, fetched when
 * the Quick Note opens and never stored. `unavailable` means it could not be
 * loaded.
 */
export type PatientQuickNoteInHealthie =
  | { status: "ok"; text: string }
  | { status: "unavailable" };

export interface PatientQuickNoteResponse {
  document: RichTextDocument;
  revision: PatientQuickNoteRevisionResponse | null;
  /**
   * NO-784: GET only, and only while patient-quick-note-healthie-import is on
   * for the RD. Null when Healthie has no other text or no linked Client.
   */
  inHealthie?: PatientQuickNoteInHealthie | null;
}

export interface PatientQuickNoteSaveRequest {
  document: RichTextDocument;
  baseRevisionId: string | null;
  saveId: string;
}

export interface PatientQuickNoteConflictResponse {
  error: "Patient Quick Note conflict";
  code: "patient_quick_note_conflict";
  current: PatientQuickNoteResponse;
}

/**
 * NO-750: a projection still pending after this long never reached the
 * worker (an enqueue failed), so the RD may send it again. The server's retry
 * rule and the client's Retry button share this one threshold.
 */
export const PATIENT_QUICK_NOTE_SYNC_STALE_PENDING_SECONDS = 120;

/**
 * NO-750: the current Revision's copy in Healthie's quick notes. Null means
 * it has never been sent. `code` is a closed safe code, never Healthie text.
 */
export interface PatientQuickNoteHealthieSyncResponse {
  status: "pending" | "running" | "succeeded" | "superseded" | "conflict" | "failed";
  code:
    | "managed_block_duplicate"
    | "managed_block_malformed"
    | "managed_block_marker_in_note"
    | "healthie_patient_unresolved"
    | "healthie_patient_ambiguous"
    | "healthie_client_not_found"
    | "healthie_unavailable"
    | "healthie_rejected"
    | "lease_expired"
    | null;
  updatedAt: string;
}

// --- Patient page (NO-847) ---
// One Client's page: a header, About them (the Patient Quick Note) and the
// scope Provider's appointments with the RD-approved SOAP excerpts. Every
// missing value is an omitted key, never null and never an empty string.

/** NO-847: who referred the Client, when that Provider is still live. */
export interface PatientPageReferrer {
  name: string;
  practiceName?: string;
}

/** NO-847: the facts at the top of the page. */
export interface PatientPageHeader {
  /** Display name, else first and last name; absent only when none is stored. */
  name?: string;
  /** At most two uppercase letters; absent with `name`. */
  initials?: string;
  /** Whole years in the practice time zone; absent with no or a future birth date. */
  age?: number;
  insuranceDescription?: string;
  referredBy?: PatientPageReferrer;
  /** Earliest `startsAt` among the page's appointments. */
  firstVisitAt?: string;
  /** Earliest `startsAt` after now among the page's appointments. */
  nextVisitAt?: string;
}

/**
 * NO-847: the Subjective and Plan sections of an appointment's SOAP Draft,
 * as Markdown, only while the RD's approval covers the current version. An
 * approved draft with neither section is `{}`.
 */
export interface PatientPageSoapExcerpt {
  subjective?: string;
  plan?: string;
}

/** NO-847: one appointment on the page, newest first. */
export interface PatientPageAppointment {
  id: string;
  startsAt: string;
  appointmentType: string;
  lengthMinutes?: number;
  soap?: PatientPageSoapExcerpt;
}

/**
 * NO-847: the Client's Patient Quick Note as the Client-keyed Quick Note GET
 * returns it, plus the newest page appointment at or before the current
 * Revision. Own lane only, and only while patient-quick-note is on.
 */
export interface PatientPageAboutThem extends PatientQuickNoteResponse {
  updatedAfter?: { appointmentId: string; startsAt: string };
}

/** NO-847: GET /api/patients/:patientId. */
export type PatientPageResponse =
  | {
      status: "ok";
      header: PatientPageHeader;
      aboutThem?: PatientPageAboutThem;
      appointments: PatientPageAppointment[];
    }
  /** An admin who has named no RD; carries no Client facts. */
  | { status: "no_selection" }
  | { status: "unavailable"; reason: "provider_unavailable" };

// --- Quick facts and Goals (NO-852) ---
// Two short lists the Client's own RD writes on the patient page, each read
// and saved on its own route with the same shapes.

/** At most this many items per list; the server refuses more. */
export const QUICK_FACTS_AND_GOALS_MAX_ITEMS = 20;
/** At most this many characters per item, after trimming. */
export const QUICK_FACTS_AND_GOALS_MAX_ITEM_LENGTH = 200;

/** GET and a successful PUT. An empty list never saved is `{ items: [], version: 0 }`. */
export interface QuickFactsAndGoalsListResponse {
  items: string[];
  version: number;
}

/** PUT body: the whole list and the version it was edited from. */
export interface QuickFactsAndGoalsSaveRequest {
  items: string[];
  baseVersion: number;
}

/** 409: the list changed since `baseVersion`; `current` is what is stored now. */
export interface QuickFactsAndGoalsConflictResponse {
  error: "List changed";
  code: "patient_list_conflict";
  current: QuickFactsAndGoalsListResponse;
}

// --- Client Red Flags (NO-846) ---

/** One critical clinical Red Flag as the Session Analysis wrote it. */
export interface PatientRedFlagText {
  lede: string;
  body: string;
}

/**
 * The Client's newest open critical clinical Red Flag: every critical clinical
 * flag of one Session Analysis, in Analysis order. `analysisRevisionId` is
 * null for a legacy Session with no revision. `raisedAt` is when that Analysis
 * became available; `sessionDate` is the Session's visit time, or its recorded
 * date, or null.
 */
export interface PatientOpenRedFlag {
  sessionId: string;
  appointmentId: string | null;
  analysisRevisionId: string | null;
  sessionDate: string | null;
  raisedAt: string;
  flags: PatientRedFlagText[];
}

/** `GET /api/patients/:patientId/red-flags` (`{ data }` envelope). */
export interface PatientRedFlagsResponse {
  openFlag: PatientOpenRedFlag | null;
}

/**
 * `POST /api/patients/:patientId/red-flags/called`: the open flag the RD called
 * about, echoed exactly from the `openFlag` the RD was shown. A flag whose
 * Analysis changed since (a newer raised time) is no longer the one shown.
 */
export interface PatientRedFlagCalledRequest {
  sessionId: string;
  analysisRevisionId: string | null;
  raisedAt: string;
}

export interface PatientRedFlagFollowUpResponse {
  id: string;
  sessionId: string;
  analysisRevisionId: string | null;
  calledAt: string;
  calledByUserId: string;
}

/** The `{ data }` of a recorded (201) or already recorded (200) Mark called. */
export interface PatientRedFlagCalledResponse {
  followUp: PatientRedFlagFollowUpResponse;
}

/** 409: the named flag is no longer the Client's open flag; reload and show the current one. */
export interface PatientRedFlagNotOpenResponse {
  error: "Red flag is not open";
  code: "red_flag_not_open";
}

// --- Client notification choices (NO-848) ---
// One Client's notification settings as the RD sees them: every message type
// on every setting channel, resolved from the Client's own choice, then the
// RD's defaults, then the practice defaults, with the layer each value came
// from. Saving stores the Client's choice, entered by staff.

/** The layer a resolved value came from; `none` means no layer set it (reads as off). */
export type NotificationSettingSourceLayer = "client" | "rd" | "practice" | "none";

export interface ClientNotificationChoice {
  enabled: boolean | null;
  offsetsMinutes: number[] | null;
  /** `client` is a choice the Client made themselves (NO-885); it has no entering User. */
  source: "staff" | "client" | null;
  enteredByUserId: string | null;
  enteredAt: string;
}

export interface ClientNotificationSetting {
  kind: string;
  channel: string;
  /** True for the reminder, the only kind with timing. */
  timed: boolean;
  enabled: boolean;
  enabledFrom: NotificationSettingSourceLayer;
  /** Minutes before the visit, longest first; null when no layer set timing. */
  offsetsMinutes: number[] | null;
  timingFrom: NotificationSettingSourceLayer;
  /** The Client choice's version a save must name; 0 when the Client has none. */
  version: number;
  clientChoice: ClientNotificationChoice | null;
}

export interface ClientNotificationSettingsResponse {
  patientId: string;
  /** The RD whose defaults fill what the Client never set. */
  rdId: string;
  /** Healthie's fixed reminder menu, in minutes before the visit, longest first. */
  timingMenuMinutes: number[];
  settings: ClientNotificationSetting[];
}

export interface ClientNotificationSettingChange {
  kind: string;
  channel: string;
  enabled: boolean | null;
  offsetsMinutes: number[] | null;
  expectedVersion: number;
}

export interface ClientNotificationSettingsSaveRequest {
  changes: ClientNotificationSettingChange[];
}

export interface ClientNotificationSettingsRefusalResponse {
  error: string;
  code: string;
  /** The offending change's index, when one change is at fault. */
  changeIndex: number | null;
}

export interface ClientNotificationSettingsConflictResponse {
  error: string;
  code: "version_conflict";
  kind: string;
  channel: string;
  currentVersion: number;
  current: ClientNotificationSettingsResponse;
}

// --- RD and practice notification defaults (NO-834) ---
// One defaults layer as its editor sees it: every Notification Kind on every
// setting channel, with this layer's own values (null = not set here), the
// version a save must name, and the value that results once the practice
// defaults fill what this layer leaves unset. Saves compare-and-set each row.

export interface NotificationDefaultsValue {
  enabled: boolean | null;
  offsetsMinutes: number[] | null;
}

export interface NotificationDefaultsSetting {
  kind: string;
  channel: string;
  /** True for the reminder, the only kind with timing. */
  timed: boolean;
  /** This layer's own row; both fields null when the layer has no row. */
  value: NotificationDefaultsValue;
  /** The row's version a save must name; 0 when the layer has no row. */
  version: number;
  enabled: boolean;
  enabledFrom: NotificationSettingSourceLayer;
  offsetsMinutes: number[] | null;
  timingFrom: NotificationSettingSourceLayer;
  /**
   * What applies where this layer leaves a field unset: the practice default
   * for an RD layer; off with no timing for the practice layer.
   */
  inherited: { enabled: boolean; offsetsMinutes: number[] | null };
}

export type NotificationDefaultsLayer =
  | { type: "practice" }
  | { type: "rd"; rdId: string };

export interface NotificationDefaultsResponse {
  layer: NotificationDefaultsLayer;
  /** Healthie's fixed reminder menu, in minutes before the visit, longest first. */
  timingMenuMinutes: number[];
  settings: NotificationDefaultsSetting[];
}

export interface NotificationDefaultsConflictResponse {
  error: string;
  code: "version_conflict";
  kind: string;
  channel: string;
  currentVersion: number;
  current: NotificationDefaultsResponse;
}

/** One RD the practice-defaults editor can pick. */
export interface NotificationDefaultsRdOption {
  id: string;
  name: string;
}

// --- The RD's own alerts (NO-834 over NO-832) ---

export interface MyAlertsPreferences {
  reminderEnabled: boolean;
  /** Minutes before each visit; shown, never edited on the page. */
  reminderLeadMinutes: number;
  bookedEnabled: boolean;
  cancelledEnabled: boolean;
  rescheduledEnabled: boolean;
  agendaEnabled: boolean;
  /** Minutes after local midnight, 0 to 1439; null until the RD picks a time. */
  agendaMinuteOfDay: number | null;
  /** Canonical IANA zone the agenda time is in. */
  agendaTimeZone: string | null;
}

export interface MyAlertsResponse {
  preferences: MyAlertsPreferences;
  /** The version a save must name; 0 when the RD has no row yet. */
  version: number;
  /** NDD sends this RD's alerts; until then Healthie still does. */
  switchedOn: boolean;
}

export interface MyAlertsConflictResponse {
  error: string;
  code: "version_conflict";
  currentVersion: number;
  current: MyAlertsResponse;
}

// --- RD hours (NO-867) ---
// An RD's bookable hours, in the RD's own zone and wall clock. Restated from
// `@ndd/core-data`'s rd-hours module, which the client bundle must not import
// (its package root carries the Drizzle schema).

export type RdHoursWeekdayName =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

/** Times are `HH:MM` on the 30-minute step, `00:00` to `24:00`. */
export interface RdHoursLunchBreakBody {
  start: string;
  minutes: 30 | 60;
}

export interface RdHoursBlockBody {
  start: string;
  end: string;
  lunchBreak: RdHoursLunchBreakBody | null;
}

/** Days keyed by weekday; on a one-week change, a day with no blocks is off. */
export type RdHoursDaysBody = Partial<Record<RdHoursWeekdayName, RdHoursBlockBody[]>>;

/** GET and a successful PUT of `/api/rd-hours`: the every-week hours. */
export interface RdHoursResponse {
  rdId: string;
  /** Canonical IANA zone; `America/New_York` until the RD saves one. */
  timeZone: string;
  /** The version a save must name; 0 before the first save. */
  version: number;
  /** The Monday the newest every-week hours start; null before the first save. */
  appliesFrom: string | null;
  /** The Monday a save made now would start from. */
  nextWeekStart: string;
  template: {
    days: Record<RdHoursWeekdayName, RdHoursBlockBody[]>;
    bookableMinutes: number;
    dayBookableMinutes: Record<RdHoursWeekdayName, number>;
  } | null;
  breakThresholdMinutes: number;
}

/** PUT `/api/rd-hours`: all seven days (a missing day is off) and the zone. */
export interface RdHoursSaveRequest {
  baseVersion: number;
  timeZone: string;
  days: RdHoursDaysBody;
}

/** 422 on either save: a stretch past the break threshold with no break. */
export interface RdHoursBreakRequiredResponse {
  error: string;
  code: "break_required";
  days: RdHoursWeekdayName[];
  message: string;
}

/** 409 on PUT `/api/rd-hours`: `current` is what is stored now. */
export interface RdHoursConflictResponse {
  error: string;
  code: "version_conflict";
  current: RdHoursResponse;
}

/** One day of a week as it will be offered. */
export interface RdHoursWeekDay {
  blocks: RdHoursBlockBody[];
  /** The one-week change replaced this day. */
  changed: boolean;
  bookableMinutes: number;
}

/** A week's one-week change: only the changed days; version 0 when none. */
export interface RdHoursWeekChangeBody {
  version: number;
  days: RdHoursDaysBody;
}

/** A visit booked in the week. Times are UTC instants. */
export interface RdHoursBookedVisit {
  appointmentId: string;
  startsAt: string;
  endsAt: string;
  clientName: string | null;
  /** Not fully inside one block, or overlapping that block's lunch break. */
  outsideHours: boolean;
}

/** GET `/api/rd-hours/weeks/:weekStart`. */
export interface RdHoursWeekResponse {
  rdId: string;
  weekStart: string;
  weekEnd: string;
  timeZone: string;
  days: Record<RdHoursWeekdayName, RdHoursWeekDay>;
  bookableMinutes: number;
  change: RdHoursWeekChangeBody;
  /** Null when the viewer has no Appointment reach for this RD. */
  bookedVisits: RdHoursBookedVisit[] | null;
  bookedVisitsHidden: boolean;
}

/** PUT `/api/rd-hours/weeks/:weekStart`: the changed days only; `{}` clears. */
export interface RdHoursWeekSaveRequest {
  baseVersion: number;
  days: RdHoursDaysBody;
}

export interface RdHoursWeekSaveResponse {
  weekStart: string;
  change: RdHoursWeekChangeBody;
  days: Record<RdHoursWeekdayName, RdHoursWeekDay>;
  bookableMinutes: number;
}

/** 409 on PUT `/api/rd-hours/weeks/:weekStart`: the week's change as stored now. */
export interface RdHoursWeekConflictResponse {
  error: string;
  code: "version_conflict";
  current: RdHoursWeekChangeBody;
}

// --- RD blocked time (NO-871) ---
// Restated from `@ndd/core-data` (`rd-time-blocks.ts`) so client code never
// imports it. Every response body is wrapped in `{ data }`.

export type RdTimeBlockKindName = "break" | "project" | "personal";

export type RdTimeBlockRepeatName = "none" | "weekly";

/**
 * POST /api/rd-schedule/blocks[?rdId=]. Times are `HH:MM` on the quarter hour
 * in the RD's zone (`end` may be `24:00`); a missing repeat is `none`;
 * `repeatUntil` (inclusive) is for weekly time only; project time needs a
 * description, the others take none.
 */
export interface RdTimeBlockCreateBody {
  kind: RdTimeBlockKindName;
  date: string;
  start: string;
  end: string;
  repeat?: RdTimeBlockRepeatName;
  repeatUntil?: string | null;
  description?: string | null;
}

/** One day a block applies to, with the block's own fields. */
export interface RdTimeBlockBody {
  id: string;
  kind: RdTimeBlockKindName;
  /** The occurrence's local date in the RD's zone. */
  date: string;
  start: string;
  end: string;
  /** ISO instants. */
  startsAt: string;
  endsAt: string;
  repeat: RdTimeBlockRepeatName;
  repeatUntil: string | null;
  /** Project time only; null otherwise. */
  description: string | null;
  /** The block's first date. */
  startsOn: string;
}

/** GET /api/rd-schedule/blocks?week=YYYY-MM-DD[&rdId=]: one Monday week in the RD's zone. */
export interface RdTimeBlockWeekResponse {
  rdId: string;
  weekStart: string;
  /** The week's Sunday. */
  weekEnd: string;
  timeZone: string;
  blocks: RdTimeBlockBody[];
  /** Project occurrences in the week; breaks and personal time never count. */
  totals: { projectMinutes: number; projectBlockCount: number };
}

/** POST answers 201 with the created block, placed on its first day. */
export type RdTimeBlockCreateResponse = RdTimeBlockBody;

export type RdTimeBlockRefusalReason =
  | "not_an_object"
  | "invalid_kind"
  | "invalid_date"
  | "invalid_time"
  | "end_not_after_start"
  | "invalid_repeat"
  | "invalid_repeat_until"
  | "description_required"
  | "description_not_allowed"
  | "description_too_long";

/** 422: what went wrong, and a fixed sentence saying how to fix it. */
export interface RdTimeBlockInvalidResponse {
  error: "invalid_block";
  reason: RdTimeBlockRefusalReason;
  message: string;
}

// --- Previous signed SOAP Note browsing (NO-454) ---

/** How `signedAt` was established: a Healthie signing or a SimplePractice import date. */
export type AppointmentPreviousSoapNoteSignedAtBasis =
  | "signed"
  | "completed"
  | "created";

export interface AppointmentPreviousSoapNoteSummary {
  id: string;
  serviceDateAt: string;
  signedAt: string;
  /** The server always sends it; an absent basis reads as "signed". */
  signedAtBasis?: AppointmentPreviousSoapNoteSignedAtBasis;
}

export interface AppointmentPreviousSoapNoteContent extends AppointmentPreviousSoapNoteSummary {
  content: string;
}

/** Label the server prefixes to a Social update preview; the client styles it. */
export const PREVIOUS_SOAP_NOTE_SOCIAL_UPDATE_LABEL = "Social update: ";

/**
 * A listed note: summary plus a short server-derived preview, never content.
 * `preview` is null for a SimplePractice import (`signedAtBasis` completed or
 * created), which gets no derived preview.
 */
export interface AppointmentPreviousSoapNoteListed extends AppointmentPreviousSoapNoteSummary {
  preview: string | null;
}

/** `GET /api/appointments/:appointmentId/previous-soap-notes`. */
export type AppointmentPreviousSoapNotesResponse =
  | { status: "empty"; previousSoapNotesScope: string }
  | {
      status: "available";
      previousSoapNotesScope: string;
      notes: AppointmentPreviousSoapNoteListed[];
      latest: AppointmentPreviousSoapNoteContent;
    };

/** `POST /api/appointments/:appointmentId/previous-soap-notes/select`. */
export interface AppointmentPreviousSoapNoteSelectRequest {
  noteId: string;
}

export interface AppointmentPreviousSoapNoteSelectResponse {
  previousSoapNotesScope: string;
  note: AppointmentPreviousSoapNoteContent;
}

export interface ChatMessageResponse {
  id: string;
  role: "user" | "assistant";
  kind: "message" | "read";
  content: string;
  sessionId: string | null;
  status: "ok" | "error";
  errorMessage: string | null;
  createdAt: string;
}

export interface ObservationImageResponse {
  id: string;
  contentType: "image/jpeg" | "image/png" | "image/webp";
  byteSize: number;
  width: number | null;
  height: number | null;
  createdAt: string;
  url: string;
}

export interface ObservationResponse {
  id: string;
  tag: "win" | "obstacle" | "risk" | "na";
  title: string | null;
  body: string;
  observationDate: string;
  createdAt: string;
  updatedAt: string;
  authorName: string;
  images: ObservationImageResponse[];
}

export interface MeetingNoteShareResponse {
  id: string;
  senderName: string;
  recipientName: string;
  status: "attempted" | "sent" | "failed";
  createdAt: string;
  sentAt: string | null;
}

export interface MeetingNoteResponse {
  id: string;
  title: string | null;
  meetingDate: string;
  fathomUrl: string | null;
  bodyJson: object;
  templateKey: string;
  templateVersion: string;
  lastSharedAt: string | null;
  createdAt: string;
  updatedAt: string;
  supervisorUserId: string;
  supervisorName: string;
  authorUserId: string;
  authorName: string;
  shareHistory: MeetingNoteShareResponse[];
}

export interface AdminRdResponse {
  id: string;
  slug: string;
  name: string;
  supervisedByUserId: string | null;
  supervisorName: string | null;
  sessionCount: number;
  chatCount: number;
  observationCount: number;
}

export interface UsageResponse {
  days: 7 | 14 | 30 | 90;
  totals: {
    users: number;
    sessions: number;
    chats: number;
    observations: number;
    meetingNotes: number;
  };
  users: Array<{
    id: string;
    name: string;
    role: AlwaysAiRole;
    sessionCount: number;
    chatCount: number;
    observationCount: number;
    meetingNoteCount: number;
  }>;
}

/** `GET /api/features` — active server-evaluated feature flags keyed by PostHog key. */
export type FeaturesResponse = Record<string, boolean>;

// --- RD workspace surface ---

export interface RdOption {
  id: string;
  slug: string;
  name: string;
}

export interface RdOptionsResponse {
  data: RdOption[];
}

export interface WorkspaceThreadSummary {
  chatId: string;
  title: string | null;
  kind: "adhoc" | "playbook_run";
  updatedAt: string;
}

export interface WorkspaceOtherOwnerThreads {
  ownerUserId: string;
  ownerName: string;
  threads: WorkspaceThreadSummary[];
}

/**
 * `GET /api/rds/:id/chats` — the workspace sidebar record/thread bootstrap.
 * `otherOwners` is populated for
 * admins only (metadata-only, never message bodies). RD switcher options come
 * from `GET /api/rds/options`.
 */
export interface WorkspaceSummaryResponse {
  rdId: string;
  rdSlug: string;
  rdName: string;
  sessionCount: number;
  observationCount: number;
  meetingNoteCount: number;
  formSnapshotCount: number;
  threads: WorkspaceThreadSummary[];
  otherOwners: WorkspaceOtherOwnerThreads[];
}

export interface WorkspaceThreadMessage {
  id: string;
  role: "user" | "assistant";
  kind: "message" | "read";
  content: string;
  sessionId: string | null;
  status: "ok" | "error";
  errorMessage: string | null;
  createdAt: string;
  sessionDate: string | null;
  clientName: string | null;
  summary: string | null;
  sessionStatus: "pending" | "analyzing" | "complete" | "error" | null;
  sessionErrorMessage: string | null;
  formSnapshotId: string | null;
  formType: string | null;
  formPeriodStartDate: string | null;
  observationNoteId: string | null;
  observationDate: string | null;
  observationTag: "win" | "obstacle" | "risk" | "na" | null;
  observationTitle: string | null;
  meetingNoteId: string | null;
  meetingDate: string | null;
  meetingNoteTitle: string | null;
  sourceDeletedType: "session" | "form" | "observation" | "meeting_note" | null;
  sourceDeletedId: string | null;
}

/**
 * `GET /api/rds/:id/chats/:chatId/messages` — one thread's messages + metadata.
 * `ownerName` is set only when the caller is not the thread owner (an admin
 * reading read-only); otherwise null.
 */
export interface WorkspaceThreadResponse {
  chatId: string;
  title: string | null;
  kind: "adhoc" | "playbook_run";
  createdAt: string;
  updatedAt: string;
  ownerName: string | null;
  messages: WorkspaceThreadMessage[];
}

// --- Appointment documentation readiness (NO-478) ---

/** Mirrors `server/clinical-documentation/contracts/appointment-documentation.ts`. */
export type AppointmentTranscriptStateResponse =
  | "waiting"
  | "ready"
  | "failed"
  | "conflict";

export type AppointmentEnhancementStateResponse =
  | "idle"
  | "claimed"
  | "generating"
  | "draft_ready"
  | "failed"
  | "cancelled";

/**
 * What the RD may do about a failed Healthie delivery (NO-541).
 * `copy_by_hand`: nothing reached the chart, so Copy note is a safe fallback.
 * `needs_help`: a note may already sit on the chart; never add one by hand.
 */
export type AppointmentWritebackNextStepResponse = "copy_by_hand" | "needs_help";

/**
 * Delivery state of the current approval's Healthie writeback (NO-541).
 * Mirrors `contracts/appointment-documentation.ts`: closed codes and one
 * Healthie note identifier only — never a vendor message, a Patient
 * identifier, or SOAP content. Null when the Draft is not approved or its
 * approval froze no Writeback Intent.
 */
export type AppointmentWritebackStateResponse =
  | { state: "in_progress" }
  | { state: "delivered"; healthieNoteId: string }
  | { state: "collision"; healthieNoteId: string }
  | { state: "failed"; nextStep: AppointmentWritebackNextStepResponse };

/**
 * `GET /api/appointments/:appointmentId/documentation` — states and opaque
 * identifiers only; Transcript content never rides this response.
 */
export interface AppointmentDocumentationSnapshotResponse {
  appointmentId: string;
  transcriptState: AppointmentTranscriptStateResponse;
  enhancementState: AppointmentEnhancementStateResponse;
  enhancementJobId: string | null;
  /** The current technical generation attempt; retries keep the job id but get a fresh generationId. */
  generationId: string | null;
  draftRevisionId: string | null;
  writeback: AppointmentWritebackStateResponse | null;
  /**
   * NO-775: where the Enhancement's frozen Transcript came from. Absent until
   * an Enhancement exists.
   */
  generationTranscriptSource?: "healthie" | "rd_pasted";
  /**
   * NO-775: a Healthie Transcript Version newer than the one the Enhancement
   * used is now current. States only; never which transcript or its text.
   */
  newerHealthieTranscript?: boolean;
  /**
   * NO-785: whether any Version of this Draft was ever approved. Present once
   * a Draft exists; absent reads as false.
   */
  draftEverApproved?: boolean;
  /**
   * NO-785: the latest RD Re-enhance. `running` while it generates; `failed`
   * or `cancelled` only while the Draft head is still the Version it was
   * replacing and nothing was ever approved; otherwise null. Absent reads as
   * null.
   */
  reEnhancementState?: AppointmentReEnhancementStateResponse | null;
}

export type AppointmentReEnhancementStateResponse = "running" | "failed" | "cancelled";

/**
 * NO-772: one NDJSON line of `GET /api/appointments/:appointmentId/documentation/stream`.
 * `writing` names the generation whose model output began; `settled` is always
 * the last line and means "re-read the documentation snapshot". No line ever
 * carries Draft, Transcript, or model text.
 */
export type AppointmentDocumentationStreamEvent =
  | { type: "writing"; generationId: string }
  | { type: "heartbeat" }
  | { type: "settled" };

/**
 * NO-772 scope change: one NDJSON line of
 * `GET /api/appointments/:appointmentId/draft/stream`, a clinical read. `text`
 * carries draft text (PHI) for one run of the named generation; `settled` is
 * always the last line.
 */
export type AppointmentDraftTextStreamEvent =
  | {
      type: "text";
      generationId: string;
      runId: string;
      sequence: number;
      textDelta: string;
    }
  | { type: "heartbeat" }
  | { type: "settled" };

/** `POST /api/appointments/:appointmentId/enhance` — claimKey makes retried requests idempotent. */
export interface AppointmentEnhanceRequest {
  claimKey: string;
}

export type AppointmentEnhanceResponse =
  | { status: "claimed"; enhancementJobId: string }
  | { status: "already_active"; enhancementJobId: string }
  | { status: "already_complete"; enhancementJobId: string };

/**
 * NO-775: `POST /api/appointments/:appointmentId/manual-generation` — generate
 * the SOAP Draft from a pasted Transcript when Healthie has none. `transcript`
 * is required and non-blank; `priorNote` is optional, and empty keeps the
 * normal Healthie prior-note lookup. Each is at most 512 KiB of UTF-8. The
 * success body is the Enhance response.
 */
export interface AppointmentManualGenerationRequest {
  claimKey: string;
  transcript: string;
  priorNote?: string;
}

export type AppointmentManualGenerationResponse = AppointmentEnhanceResponse;

/**
 * NO-785: `POST /api/appointments/:appointmentId/re-enhance` — run generation
 * again over a never-approved Draft. `draftRevisionId` is the Draft head the
 * RD saw when they confirmed; the claim key makes a retried request
 * idempotent.
 */
export interface AppointmentReEnhanceRequest {
  claimKey: string;
  draftRevisionId: string;
}

export type AppointmentReEnhanceResponse =
  | { status: "claimed"; enhancementJobId: string }
  | { status: "already_active"; enhancementJobId: string };

/** The 409 refusals of a Re-enhance, discriminated by `code`. */
export type AppointmentReEnhanceRefusalResponse =
  | { error: string; code: "draft_changed"; currentDraftRevisionId: string }
  | { error: string; code: "draft_approved" }
  | { error: string; code: "no_draft" };

/** The closed `code` of a refused manual generation request. */
export type AppointmentManualGenerationErrorCode =
  | "invalid_request"
  | "field_too_large"
  | "manual_generation_unavailable"
  | "transcript_exists"
  | "appointment_identity_incomplete"
  | "appointment_identity_ambiguous"
  | "appointment_identity_unresolved"
  | "session_conflict"
  | "generation_not_started";
/**
 * `POST /api/appointments/:appointmentId/presence` — renews this tab's
 * presence lease on the Appointment documentation surface. `tabId` is one
 * browser-generated lowercase UUID per tab. Success is a bare 204: presence
 * reads are server-internal and never reach the browser.
 */
export interface AppointmentPresenceHeartbeatRequest {
  tabId: string;
}

// --- SOAP Draft editing (NO-479) ---

/**
 * One immutable SOAP Draft revision's identity. Opaque ids and metadata only;
 * the revision's clinical content rides `document` on the authorized
 * `AppointmentDraftEditableResponse` and nowhere else.
 */
export interface AppointmentDraftRevisionResponse {
  id: string;
  sequence: number;
  createdAt: string;
  digest: string;
}

export interface SoapDraftApprovalResponse {
  id: string;
  draftRevisionId: string;
  approvedAt: string;
}

export type SoapDraftReviewResponse =
  | { state: "draft"; approval: "available" }
  | {
      state: "draft";
      approval: "unavailable";
      reason: "appointmentless_session";
    }
  | { state: "approved"; approval: SoapDraftApprovalResponse };

/**
 * Why a Draft cannot be edited. `not_generated` means no Draft revision with
 * machine provenance exists for this Appointment's Session yet;
 * `document_unsupported` means the generated Version's content is not a
 * rich-text document the editor can mount (NO-475 owns that conversion), so
 * the surface stays read-only instead of coercing content.
 */
export type AppointmentDraftNotEditableReason =
  | "not_generated"
  | "document_unsupported"
  /** NO-785: a Re-enhance is running; saves wait until it ends. */
  | "re_enhancing";

/**
 * Source facts behind the Draft's machine Version: when it was generated and
 * which optional inputs the frozen SOAP Input Snapshot recorded. Facts only —
 * the workspace footer names the sources without any of their content.
 */
export interface SoapDraftProvenanceResponse {
  generatedAt: string;
  usedAppointmentNote: boolean;
  priorSignedNoteAt: string | null;
  /** NO-775: where the draft's Transcript came from. */
  transcriptSource: "healthie" | "rd_pasted";
  /** NO-775: where its prior note came from; null when none was used. */
  priorNoteSource: "healthie" | "rd_pasted" | null;
}

export interface AppointmentDraftEditableResponse {
  status: "editable";
  document: RichTextDocument;
  revision: AppointmentDraftRevisionResponse;
  review: SoapDraftReviewResponse;
  provenance: SoapDraftProvenanceResponse;
}

/** `GET /api/appointments/:appointmentId/draft` — private, no-store. */
export type AppointmentDraftResponse =
  | AppointmentDraftEditableResponse
  | { status: "not_editable"; reason: AppointmentDraftNotEditableReason };

/**
 * `PUT /api/appointments/:appointmentId/draft`. `baseRevisionId` is the
 * revision this edit was typed against: the server accepts the save only while
 * it is still the Draft's head, so a second tab can never silently overwrite a
 * newer revision.
 */
export interface AppointmentDraftSaveRequest {
  document: RichTextDocument;
  baseRevisionId: string;
}

export type AppointmentDraftSaveResponse = AppointmentDraftEditableResponse;

/** Both refusals answer 409 and are discriminated by `code`. */
export type AppointmentDraftSaveRefusalResponse =
  | {
      error: "SOAP Draft conflict";
      code: "appointment_draft_conflict";
      current: AppointmentDraftEditableResponse;
    }
  | {
      error: "SOAP Draft is not editable";
      code: "appointment_draft_not_editable";
      reason: AppointmentDraftNotEditableReason;
    };

export interface SoapDraftApproveRequest {
  draftRevisionId: string;
}

export type SoapDraftApprovalNotReadyReason =
  | "no_draft"
  | "no_generation_provenance"
  | "document_unsupported"
  /** NO-785: a Re-enhance is running; approval waits until it ends. */
  | "re_enhancing";

export type SoapDraftApproveRefusalResponse =
  | {
      error: "SOAP Draft changed";
      code: "soap_draft_approval_conflict";
      current: AppointmentDraftResponse;
    }
  | {
      error: "SOAP Draft cannot be approved";
      code: "soap_draft_approval_not_ready";
      reason: SoapDraftApprovalNotReadyReason;
    }
  | {
      error: "SOAP Draft cannot be approved";
      code: "soap_draft_approval_refused";
      reason: "appointmentless_session";
    };
